import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { FaSearch, FaPlus, FaClock, FaMapMarkerAlt, FaEye, FaComments, FaBox, FaTools, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'sonner';
import DistanceFilter from '../components/DistanceFilter';
import PageSeo from '../components/PageSeo';
import { SEO } from '../utils/seoConfig';

export default function Requests() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [requestType, setRequestType] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [centerCity, setCenterCity] = useState('');
  const [radiusKm, setRadiusKm] = useState(null);

  useEffect(() => {
    fetchRequests();
    fetchCategories();
  }, [requestType, categoryFilter, centerCity, radiusKm]);

  const fetchRequests = async () => {
    try {
      let url = `${API}/requests/public/latest?limit=50`;
      if (requestType) url += `&request_type=${requestType}`;
      if (categoryFilter) url += `&category_id=${categoryFilter}`;
      if (centerCity && radiusKm) {
        url += `&center_city=${encodeURIComponent(centerCity)}&radius_km=${radiusKm}`;
      }
      
      const response = await axios.get(url);
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Admin actions
  const handleDeleteRequest = async (requestId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm(i18n.language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette demande ?' 
      : 'Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await axios.delete(`${API}/requests/delete/${requestId}?user_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Demande supprimée' : 'Request deleted');
      fetchRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting request');
    }
  };

  const handleEditRequest = (requestId, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Redirige vers la page d'édition avec user_id en paramètre
    navigate(`/user/requests/edit/${requestId}`);
  };

  // Check if user is admin
  const isAdmin = user && (user.roles?.includes('admin') || user.role === 'admin');

  // Handler pour le filtre de distance
  const handleDistanceFilter = (city, radius) => {
    console.log('Distance filter applied:', { city, radius });
    setCenterCity(city || '');
    setRadiusKm(city && radius ? radius : null);
  };

  // Filtrer et trier par date (plus récentes en premier)
  const filteredRequests = requests
    .filter(req => {
      const title = i18n.language === 'fr' ? req.title_fr : req.title_en;
      const description = i18n.language === 'fr' ? req.description_fr : req.description_en;
      return title.toLowerCase().includes(searchTerm.toLowerCase()) ||
             description.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      // Sort by position first (lower position = higher priority)
      const posA = a.position ?? 999;
      const posB = b.position ?? 999;
      if (posA !== posB) return posA - posB;
      // Then by date (newer first)
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const getTypeIcon = (type) => {
    return type === 'product' ? <FaBox /> : <FaTools />;
  };

  const getTypeLabel = (type) => {
    if (type === 'product') {
      return i18n.language === 'fr' ? 'Produit' : 'Product';
    }
    return i18n.language === 'fr' ? 'Service' : 'Service';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <PageSeo {...SEO.requests} />
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
              {i18n.language === 'fr' ? 'Les Dernières Demandes' : 'Latest Requests'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'fr' 
                ? 'Découvrez ce que les professionnels recherchent' 
                : 'Discover what professionals are looking for'}
            </p>
          </div>
          
          {user && (
            <Link to="/user/requests/new">
              <Button className="bg-[#635BFF] hover:bg-[#5248d9]">
                <FaPlus className="mr-2" />
                {i18n.language === 'fr' ? 'Créer une Demande' : 'Create Request'}
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={i18n.language === 'fr' ? 'Rechercher...' : 'Search...'}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <Select value={requestType || "all"} onValueChange={(value) => setRequestType(value === "all" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n.language === 'fr' ? 'Tous les types' : 'All types'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {i18n.language === 'fr' ? 'Tous les types' : 'All types'}
                    </SelectItem>
                    <SelectItem value="product">
                      {i18n.language === 'fr' ? 'Produits' : 'Products'}
                    </SelectItem>
                    <SelectItem value="service">
                      {i18n.language === 'fr' ? 'Services' : 'Services'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter */}
              <div>
                <Select value={categoryFilter || "all"} onValueChange={(value) => setCategoryFilter(value === "all" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n.language === 'fr' ? 'Toutes catégories' : 'All categories'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {i18n.language === 'fr' ? 'Toutes catégories' : 'All categories'}
                    </SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Distance Filter */}
            <div className="mt-4">
              <DistanceFilter onFilterChange={handleDistanceFilter} />
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-4 text-gray-600">
          {filteredRequests.length} {i18n.language === 'fr' ? 'demande(s) trouvée(s)' : 'request(s) found'}
        </div>

        {/* Requests Grid */}
        {filteredRequests.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">
              {i18n.language === 'fr' 
                ? 'Aucune demande pour le moment' 
                : 'No requests at the moment'}
            </p>
            {user && (
              <Link to="/user/requests/new">
                <Button className="bg-[#635BFF]">
                  <FaPlus className="mr-2" />
                  {i18n.language === 'fr' ? 'Créer la première demande' : 'Create first request'}
                </Button>
              </Link>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredRequests.map(request => (
              <Card key={request.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  {/* Image */}
                  {request.images && request.images[0] && (
                    <img 
                      src={request.images[0]} 
                      alt={i18n.language === 'fr' ? request.title_fr : request.title_en}
                      className="w-full h-40 object-cover rounded-t-lg"
                    />
                  )}
                  
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 text-sm">
                        {getTypeIcon(request.request_type)}
                        <span className="px-2 py-1 bg-[#0A2540] text-white rounded text-xs">
                          {getTypeLabel(request.request_type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        <FaEye className="text-xs" />
                        <span>{request.views}</span>
                      </div>
                    </div>

                    {/* Date */}
                    {request.created_at && (
                      <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                        <FaClock />
                        <span>
                          {new Date(request.created_at).toLocaleDateString(
                            i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                            { year: 'numeric', month: 'long', day: 'numeric' }
                          )}
                        </span>
                      </div>
                    )}

                    {/* Title */}
                    <h3 className="font-bold text-lg mb-2 text-[#0A2540]">
                      {i18n.language === 'fr' ? request.title_fr : request.title_en}
                    </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {i18n.language === 'fr' ? request.description_fr : request.description_en}
                  </p>

                  {/* Details */}
                  <div className="space-y-2 mb-4 text-sm">
                    {request.quantity && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaBox className="text-[#635BFF]" />
                        <span>
                          {i18n.language === 'fr' ? 'Quantité:' : 'Quantity:'} {request.quantity} {request.unit || ''}
                        </span>
                      </div>
                    )}
                    
                    {request.deadline && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaClock className="text-[#00D4FF]" />
                        <span>
                          {i18n.language === 'fr' ? 'Délai:' : 'Deadline:'} {request.deadline}
                        </span>
                      </div>
                    )}
                    
                    {request.location && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaMapMarkerAlt className="text-red-500" />
                        <span>{request.location}</span>
                      </div>
                    )}
                    
                    {request.budget && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-semibold">
                          {i18n.language === 'fr' ? 'Budget:' : 'Budget:'} {request.budget} €
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-between items-center pt-4 border-t">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <FaComments />
                      <span>{request.responses_count || 0}</span>
                    </div>
                    
                    <div className="flex gap-2">
                      {isAdmin && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => handleEditRequest(request.id, e)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <FaEdit className="mr-1" />
                            {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={(e) => handleDeleteRequest(request.id, e)}
                          >
                            <FaTrash className="mr-1" />
                            {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => user ? navigate(`/requests/${request.id}`) : navigate('/login')}
                      >
                        {i18n.language === 'fr' ? 'Voir détails' : 'View details'}
                      </Button>
                    </div>
                  </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
